import { GRADE_BANDS, isGradeInBand } from "../SchoolStructure/classrooms/gradeBands";
import type { GradeBand } from "../SchoolStructure/classrooms/gradeBands";
import type { Classroom } from "./types";
import { compareClassrooms, entityId } from "./utils";

export interface ClassroomGroupOption {
  kind: "group";
  value: string;
  key: GradeBand["key"];
  defaultLabel: string;
  classroomUuids: string[];
}

export interface ClassroomOption {
  kind: "classroom";
  value: string;
  classroom: Classroom;
}

const classType = (classroom: Classroom) => String(classroom.classType ?? classroom.type ?? "").toUpperCase();

// Order: kindergarten classrooms, the grade bands (lower, middle, upper), then the grade classrooms.
// A band is only a shortcut for its classrooms: it is offered when the school year has any and never saved itself.
export const buildClassroomOptions = (classrooms: Classroom[]): Array<ClassroomOption | ClassroomGroupOption> => {
  const options: ClassroomOption[] = [...classrooms]
    .sort(compareClassrooms)
    .map((classroom) => ({ kind: "classroom", value: String(entityId(classroom)), classroom }));

  const groups: ClassroomGroupOption[] = GRADE_BANDS.map((band) => ({
    kind: "group" as const,
    value: `group:${band.key}`,
    key: band.key,
    defaultLabel: band.defaultLabel,
    classroomUuids: options
      .filter(
        ({ classroom }) =>
          classType(classroom) === "GRADE" && isGradeInBand(band, classroom.grade ?? classroom.classGrade),
      )
      .map((option) => option.value),
  })).filter((group) => group.classroomUuids.length > 0);

  const isKindergarten = (option: ClassroomOption) => classType(option.classroom) === "KINDERGARTEN";

  return [...options.filter(isKindergarten), ...groups, ...options.filter((option) => !isKindergarten(option))];
};

const isGroupSelected = (group: ClassroomGroupOption, classroomUuids: string[]) =>
  group.classroomUuids.every((uuid) => classroomUuids.includes(uuid));

// Values for the <select>: the chosen classrooms plus every group that is completely chosen.
export const selectedOptionValues = (groups: ClassroomGroupOption[], classroomUuids: string[]): string[] => [
  ...classroomUuids,
  ...groups.filter((group) => isGroupSelected(group, classroomUuids)).map((group) => group.value),
];

// Translates the <select> values back to classroom uuids; (de)selecting a group (de)selects its classrooms.
export const resolveClassroomUuids = (
  selectedValues: string[],
  previousUuids: string[],
  groups: ClassroomGroupOption[],
): string[] => {
  const groupValues = new Set(groups.map((group) => group.value));
  const uuids = new Set(selectedValues.filter((value) => !groupValues.has(value)));

  groups.forEach((group) => {
    const nowSelected = selectedValues.includes(group.value);
    const wasSelected = isGroupSelected(group, previousUuids);
    if (nowSelected && !wasSelected) group.classroomUuids.forEach((uuid) => uuids.add(uuid));
    if (!nowSelected && wasSelected && isGroupSelected(group, Array.from(uuids))) {
      group.classroomUuids.forEach((uuid) => uuids.delete(uuid));
    }
  });

  return Array.from(uuids);
};
